/**
 * Platform — mapped from worker /api/v1/channels response.
 * Replaces the old fakeAccounts.ChatbotAccount type throughout the UI.
 */
export interface Platform {
  id: string;
  name: string;
  description?: string;
  platform: string;
  platform_id: string;       // Facebook Page ID / Zalo OA ID / Instagram Business ID
  avatar?: string;           // picture_url from platform
  status: 'active' | 'inactive' | 'error';
  followers: number;         // fan_count
  unread_count: number;
  last_activity: string;     // updated_at ISO string
  access_token_status: 'valid' | 'expired' | 'missing';
  /** Browser-type platforms: true when polling is actively running */
  polling_enabled: boolean;
  chatbot_mode: 'basic' | 'expert';
  // AI config
  personality?: string;
  tone?: string;
  greeting?: string;
  quickReplies?: { trigger: string; response: string }[];
  // Demographics
  demographics?: {
    ageRange?: string;
    gender?: 'all' | 'male' | 'female';
    language?: string;
    location?: string;
  };
}

/**
 * Map raw worker channel API response → Platform interface.
 * Supports both new field names (name, avatar, channel_id, platform)
 * and legacy field names (chatbot_name, page_name, page_id, platform_type)
 * for backward compatibility during migration.
 */
export function mapApiPlatform(raw: any): Platform {
  const platformType = raw.platform || raw.platform_type || 'facebook';
  const isBrowser = platformType.endsWith('_browser');

  // DB model hides access_token (json:"-"); infer validity from is_active/webhook_verified.
  // Worker API may return access_token_status directly.
  // Browser accounts never have tokens — derive status from polling_enabled instead.
  const isActive = raw.is_active !== undefined ? raw.is_active : true;
  const pollingEnabled = Boolean(raw.polling_enabled);

  const accessStatus: Platform['access_token_status'] = isBrowser
    ? (pollingEnabled ? 'valid' : 'missing')
    : (raw.access_token_status ||
       (raw.page_access_token ? 'valid'
         : (isActive || raw.webhook_verified) ? 'valid'
         : 'missing'));

  const status: Platform['status'] = isBrowser
    ? (pollingEnabled ? 'active' : 'inactive')
    : (accessStatus === 'expired' ? 'error'
       : !isActive ? 'inactive'
       : raw.status === 'inactive' ? 'inactive'
       : 'active');

  const channelId = raw.channel_id || raw.page_id || raw.id || 'default';
  const seed = String(channelId).replace(/\W/g, '');
  const platformColor: Record<string, string> = {
    facebook: '1877F2',
    zalo: '0068FF',
    instagram: 'E1306C',
  };
  const bg = platformColor[platformType.replace(/_browser$/, '')] || '3390ec';

  return {
    id: raw.id || raw._id,
    name: raw.name || raw.platform_name || raw.chatbot_name || raw.page_name || 'Unnamed',
    description: raw.chatbot_description,
    platform: platformType as Platform['platform'],
    platform_id: channelId,
    avatar:
      raw.avatar ||
      raw.page_picture_url ||
      raw.picture_url ||
      `https://api.dicebear.com/7.x/shapes/svg?seed=${seed}&backgroundColor=${bg}`,
    status,
    followers: raw.fan_count ?? 0,
    unread_count: raw.unread_count ?? 0,
    last_activity: (() => {
      const ts = raw.updated_at || raw.created_at;
      if (!ts) return new Date().toISOString();
      // Worker API stores epoch in seconds; convert to ms if needed
      const num = typeof ts === 'number' ? ts : Number(ts);
      if (!isNaN(num) && num < 1e12) return new Date(num * 1000).toISOString();
      return typeof ts === 'string' ? ts : new Date(num).toISOString();
    })(),
    access_token_status: accessStatus,
    polling_enabled: pollingEnabled,
    chatbot_mode: (raw.chatbot_mode === 'expert' ? 'expert' : 'basic') as Platform['chatbot_mode'],
    personality: raw.personality,
    tone: raw.tone,
    greeting: raw.greeting,
    quickReplies: Array.isArray(raw.quick_replies) ? raw.quick_replies : [],
    demographics: raw.demographics,
  };
}

/** Worker conversation from GET /api/v1/db/conversations */
export interface WorkerConversation {
  id: string;
  platform: string;
  channel_id: string;
  user_id: string;
  workspace_id: string;
  customer_name: string;
  customer_avatar: string;
  last_message: string;
  last_message_at: number;   // unix timestamp (float)
  unread_count: number;
  is_bot_handled: number;    // 0 | 1
  assigned_agent_id: string | null;
  status: string;            // 'active' | 'archived' | 'spam'
  labels: string;
  metadata: string;
  /** Parsed from metadata.interest_level — populated by the worker after each message */
  interest_level?: string;
  created_at: number;
  updated_at: number;
}

/** Interest stat from GET /api/v1/stats/interest */
export interface InterestStat {
  label: string;
  count: number;
}

/** Worker message from GET /api/v1/db/conversations/{id}/messages */
export interface WorkerMessage {
  id: string;
  conversation_id: string;
  platform_message_id: string;
  sender_type: 'customer' | 'bot' | 'agent';
  sender_id: string;
  content_type: string;
  content_text: string;
  content_url: string;
  content_metadata: string | Record<string, any> | null;
  is_read: number;
  sent_at: number;   // unix timestamp
  created_at: number;
}
